import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * Tenant-supplied LLM provider API keys — `GET/POST/PATCH/DELETE /v1/llm-api-keys`.
 *
 * The key itself is never part of any response: the backend stores it encrypted and has no field
 * for it on the response schema. `api_key` therefore appears only on create/update payloads, and
 * the UI shows a fixed mask rather than any part of the stored value.
 */
export type LlmProvider = "openai" | "anthropic";

export type LlmApiKey = {
  id: number;
  provider: LlmProvider;
  name: string;
  models: string[];
  created_at: string;
  updated_at: string;
};

export type LlmModelKind = "chat" | "embedding" | "image";

export type AvailableModel = {
  provider: LlmProvider;
  id: string;
  display_name: string;
  kind: LlmModelKind;
};

export type CreateLlmApiKeyPayload = {
  provider: LlmProvider;
  name: string;
  api_key: string;
  models: string[];
};

export type UpdateLlmApiKeyPayload = {
  id: number;
  name?: string;
  api_key?: string;
  models?: string[];
};

export type LlmApiKeysState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  keys: LlmApiKey[];
  modelsStatus: "idle" | "loading" | "succeeded" | "failed";
  availableModels: AvailableModel[];
};

export const llmApiKeysInitialState: LlmApiKeysState = {
  status: STATUS.IDLE,
  error: null,
  keys: [],
  modelsStatus: STATUS.IDLE,
  availableModels: [],
};

function isLlmProvider(value: unknown): value is LlmProvider {
  return value === "openai" || value === "anthropic";
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isLlmApiKey(value: unknown): value is LlmApiKey {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    isLlmProvider(row.provider) &&
    typeof row.name === "string" &&
    isStringArray(row.models) &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

function isAvailableModel(value: unknown): value is AvailableModel {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    isLlmProvider(row.provider) &&
    typeof row.id === "string" &&
    typeof row.display_name === "string" &&
    (row.kind === "chat" || row.kind === "embedding" || row.kind === "image")
  );
}

export const fetchLlmApiKeys = createAsyncThunk<LlmApiKey[], void, { rejectValue: string }>(
  "llmApiKeys/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch("/v1/llm-api-keys/", { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!Array.isArray(data) || !data.every(isLlmApiKey)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const fetchAvailableModels = createAsyncThunk<
  AvailableModel[],
  void,
  { rejectValue: string }
>("llmApiKeys/fetchAvailableModels", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/llm-api-keys/available-models", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!Array.isArray(data) || !data.every(isAvailableModel)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const createLlmApiKey = createAsyncThunk<
  LlmApiKey,
  CreateLlmApiKeyPayload,
  { rejectValue: string }
>("llmApiKeys/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/llm-api-keys/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isLlmApiKey(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const patchLlmApiKey = createAsyncThunk<
  LlmApiKey,
  UpdateLlmApiKeyPayload,
  { rejectValue: string }
>("llmApiKeys/patch", async ({ id, ...body }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/llm-api-keys/${String(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isLlmApiKey(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const deleteLlmApiKey = createAsyncThunk<number, number, { rejectValue: string }>(
  "llmApiKeys/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/llm-api-keys/${String(id)}`, { method: "DELETE" });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      return id;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

const llmApiKeysSlice = createSlice({
  name: "llmApiKeys",
  initialState: llmApiKeysInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchLlmApiKeys.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchLlmApiKeys.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.keys = action.payload;
      })
      .addCase(fetchLlmApiKeys.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(fetchAvailableModels.pending, (state) => {
        state.modelsStatus = STATUS.LOADING;
      })
      .addCase(fetchAvailableModels.fulfilled, (state, action) => {
        state.modelsStatus = STATUS.SUCCEEDED;
        state.availableModels = action.payload;
      })
      .addCase(fetchAvailableModels.rejected, (state) => {
        state.modelsStatus = STATUS.FAILED;
      })
      .addCase(createLlmApiKey.fulfilled, (state, action) => {
        state.keys = [...state.keys, action.payload];
      })
      .addCase(patchLlmApiKey.fulfilled, (state, action) => {
        state.keys = state.keys.map((key) =>
          key.id === action.payload.id ? action.payload : key,
        );
      })
      .addCase(deleteLlmApiKey.fulfilled, (state, action) => {
        state.keys = state.keys.filter((key) => key.id !== action.payload);
      });
  },
});

export const llmApiKeysReducer = llmApiKeysSlice.reducer;
